import React, { useEffect, useState } from 'react'
import { getUserData } from '@src/auth/utils'
import { useNotificationHub, createNotificationActionHandler } from '../../utility/hooks/useNotificationHub'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo, faFlag, faArrowRight, faCheck, faCheckCircle } from '@fortawesome/pro-regular-svg-icons'
import "../../@core/components/notifications/notifications.scss"
import notificationsDaysImage from '@src/assets/images/illustration/notifications-30days.svg'

const NotificationsPage = () => {
  const userData = getUserData()
  const operatorId = userData?.AccountId
  const operatorUserId = userData?.UserId
  const history = useHistory()
  const [activeTab, setActiveTab] = useState("focussed")
  const [sortOrder, setSortOrder] = useState("newest")
  const [filterType, setFilterType] = useState("all")

  // ** Redux state
  const { notifications } = useSelector(state => state.notifications)

  const { markAsRead } = useNotificationHub({
    operatorId,
    operatorUserId
  })

  const handleNotificationAction = createNotificationActionHandler({ history })

  useEffect(() => {
    if (notifications.length > 0 && notifications.some(n => !n.isRead)) {
      const unreadIds = notifications.filter(n => !n.isRead).map((n, idx) => n.id || idx)
      markAsRead(unreadIds)
    }
  }, [notifications, markAsRead])

  const getRelativeTime = (date) => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()

    // If the time difference is negative (future date), return "0 secs ago"
    if (diffMs < 0) {
      return "0 secs ago"
    }
    
    // Convert to seconds
    const diffSeconds = Math.floor(diffMs / 1000)
    
    if (diffSeconds < 60) {
      return `${diffSeconds} sec${diffSeconds === 1 ? '' : 's'} ago`
    }
    
    // Convert to minutes
    const diffMinutes = Math.floor(diffSeconds / 60)
    
    if (diffMinutes < 60) {
      return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`
    }
    
    // Convert to hours
    const diffHours = Math.floor(diffMinutes / 60)
    
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    }
    
    // Convert to days
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    }
    
    // Convert to weeks
    const diffWeeks = Math.floor(diffDays / 7)
    
    if (diffWeeks < 4) {
      return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`
    }
    
    // Convert to months (approximate)
    const diffMonths = Math.floor(diffDays / 30)
    
    if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`
    }
    
    // Convert to years (approximate)
    const diffYears = Math.floor(diffDays / 365)
    return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`
  }

  const getStatusDisplay = (notification) => {
    if (!notification.notificationDate) {
      return { text: "", className: "status-default" }
    }

    const notificationDate = new Date(notification.notificationDate)
    
    // For all other cases, show relative time
    const relativeTime = getRelativeTime(notificationDate)
    return { text: relativeTime, className: "status-default" }
  }

  const getNotificationDate = (notification) => {
    if (!notification.notificationDate) {
      return ""
    }
    const date = new Date(notification.notificationDate)
    const day = date.getDate()
    const month = date.toLocaleString('en-US', { month: 'long' })
    const year = date.getFullYear()
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    hours = hours % 12
    hours = hours ? hours : 12 // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes
    return `${day} ${month} ${year} ${hours}:${minutesStr}${ampm}`
  }

  // Get level-based status badge
  const getLevelBadge = (notification) => {
    const level = notification.level || 0
    
    switch (level) {
      case 0: // Information
        return {
          text: "Information",
          icon: faCircleInfo,
          className: "badge-info",
          color: "#1F8BFF"
        }
      case 1: // Flagged
        return {
          text: "Flagged",
          icon: faFlag,
          className: "badge-flagged",
          color: "#EA5455"
        }
      case 2: // Completed
        return {
          text: "Completed",
          icon: faCheckCircle,
          className: "badge-completed",
          color: "#04A777"
        }
      default:
        return {
          text: "Information",
          icon: faCircleInfo,
          className: "badge-info",
          color: "#1F8BFF"
        }
    }
  }

  // Filter notifications based on active tab
  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === "system") {
      return notification.isSystem === true
    } else {
      return notification.isSystem === false
    }
  })

  // Sort notifications based on sort order
  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    if (!a.notificationDate || !b.notificationDate) return 0
    
    const dateA = new Date(a.notificationDate)
    const dateB = new Date(b.notificationDate)
    
    if (sortOrder === "newest") {
      return dateB - dateA
    } else {
      return dateA - dateB
    }
  })

  // Filter by type if needed
  const finalNotifications = filterType === "all" ? sortedNotifications : sortedNotifications.filter(n => n.level === parseInt(filterType))

  const handleMarkAllAsRead = () => {
    const unreadIds = notifications.filter(n => !n.isRead).map((n, idx) => n.id || idx)
    if (unreadIds.length > 0) {
      markAsRead(unreadIds)
    }
  }

  return (
    <div className="notifications-page-container">
      <div className="notifications-title-card">
        <h2>Notifications</h2>
      </div>

      <div className="notifications-header">
        <div className="header-tabs">
          <button 
            className={`tab ${activeTab === "focussed" ? "active" : ""}`}
            onClick={() => setActiveTab("focussed")}
          >
            Focussed
          </button>
          <button 
            className={`tab ${activeTab === "system" ? "active" : ""}`}
            onClick={() => setActiveTab("system")}
          >
            System
          </button>
        </div>
        
        <div className="header-actions">
          <button className="mark-all-read" onClick={handleMarkAllAsRead}>
            <FontAwesomeIcon icon={faCheck} />
            Mark all as read
          </button>
          
          <select 
            className="filter-dropdown" 
            value={sortOrder} 
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          
          <select 
            className="filter-dropdown" 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All types</option>
            <option value="0">Information</option>
            <option value="1">Flagged</option>
            <option value="2">Completed</option>
          </select>
        </div>
      </div>
      
      <div className="notifications-content-card">
        
        {finalNotifications.length === 0 ? (
          <div className="no-notifications">
            <div className="no-notifications-icon">
              <img src={notificationsDaysImage} alt="Notifications Days" />
            </div>
            <p>You have no notifications.</p>
          </div>
        ) : (
          <div className="notifications-page-list">
            {finalNotifications.map((n, idx) => {
              const status = getStatusDisplay(n)
              const levelBadge = getLevelBadge(n)

              return (
                <div
                  key={n.id || idx}
                  className={`notification-item${n.isRead ? "" : " unread"}`}
                >
                  <div className="notification-content">
                    <div className="notification-main">
                      <div className="notification-header">
                        <div className={`notification-status ${status.className}`} title={getNotificationDate(n)}>
                          {status.text}
                          {!n.isRead && (
                            <span className="status-dot"></span>
                          )}
                        </div>
                        <div className="notification-title">
                          {n.title}
                        </div>
                        <div className="notification-message">
                          {n.message}
                        </div>
                        
                        {Array.isArray(n.actions) && n.actions.length > 0 && (
                          <div className="notification-actions">
                            {n.actions.map(action => (
                              <button
                                key={action.action}
                                className="notification-action-btn"
                                onClick={() => handleNotificationAction(n, action)}
                              >
                                {action.title}
                                <FontAwesomeIcon icon={faArrowRight} />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="notification-sidebar">
                      <div className={`status-badge ${levelBadge.className}`}>
                        <FontAwesomeIcon icon={levelBadge.icon} style={{ color: levelBadge.color }} />
                        {levelBadge.text}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
            
            {notifications.length > 0 && (
              <div className="notifications-days-image">
                <img src={notificationsDaysImage} alt="Notifications Days" />
                <p>That's all your notifications from the last 30 days.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationsPage 